import { supabase } from "../services/supabaseClient";

export type OperationsCaseRecord = Record<string, unknown> & {
  id?: string;
  reference?: string;
  title?: string;
  summary?: string;
  category?: string;
  status?: string;
  priority?: string;
  source_system?: string;
  source_reference?: string;
  created_at?: string;
  updated_at?: string;
};

export type OperationsCaseNote = Record<string, unknown> & {
  id?: string | number;
  case_id?: string;
  body?: string;
  note?: string;
  author_email?: string;
  created_at?: string;
};

export type OperationsCaseEvent = Record<string, unknown> & {
  id?: string | number;
  case_id?: string;
  event_type?: string;
  actor_email?: string;
  created_at?: string;
};

export type OperationsCasesPayload = Record<string, unknown> & {
  ok?: boolean;
  error?: string;
  cases?: OperationsCaseRecord[];
  case?: OperationsCaseRecord;
  notes?: OperationsCaseNote[];
  events?: OperationsCaseEvent[];
};

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

function normalizePayload(value: unknown): OperationsCasesPayload {
  const payload = asRecord(value) || {};
  const nested = asRecord(payload.data);
  const source = nested ? { ...payload, ...nested } : payload;
  const caseRecord = asRecord(source.case) || asRecord(source.item) ||
    (nested && typeof nested.id === "string" ? nested : null);
  const directCases = Array.isArray(payload.data) ? payload.data : (source.cases || source.items);

  return {
    ...source,
    cases: asArray<OperationsCaseRecord>(directCases),
    case: caseRecord ? (caseRecord as OperationsCaseRecord) : undefined,
    notes: asArray<OperationsCaseNote>(source.notes),
    events: asArray<OperationsCaseEvent>(source.events),
  };
}

async function invokeOperationsCases(body: Record<string, unknown>) {
  const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
  if (sessionError) throw sessionError;

  const accessToken = sessionData.session?.access_token;
  if (!accessToken) throw new Error("Session administrateur absente.");

  const { data, error } = await supabase.functions.invoke("admin-operations-cases", {
    body,
    headers: { Authorization: `Bearer ${accessToken}` },
  });

  if (error) throw error;
  const payload = normalizePayload(data);
  if (payload.ok === false) throw new Error(String(payload.error || "Opération refusée."));
  return payload;
}

export function listOperationsCases(filters: { status?: string; category?: string } = {}) {
  return invokeOperationsCases({ action: "list", ...filters, limit: 100 });
}

export function getOperationsCase(caseId: string) {
  return invokeOperationsCases({ action: "detail", case_id: caseId });
}

export function createOperationsCase(input: {
  idempotencyKey: string;
  category: string;
  priority: string;
  title: string;
  summary: string;
  sourceSystem: string;
  sourceReference: string;
  metadata?: Record<string, unknown>;
}) {
  return invokeOperationsCases({
    action: "create",
    idempotency_key: input.idempotencyKey,
    idempotencyKey: input.idempotencyKey,
    category: input.category,
    case_type: input.category,
    domain: input.category,
    priority: input.priority,
    title: input.title,
    summary: input.summary,
    description: input.summary,
    source_system: input.sourceSystem,
    source_repository: input.sourceSystem,
    source_reference: input.sourceReference,
    source_ref: input.sourceReference,
    source_id: input.sourceReference,
    metadata: input.metadata || {},
  });
}

export function addOperationsCaseNote(caseId: string, note: string) {
  return invokeOperationsCases({
    action: "add_note",
    case_id: caseId,
    note,
    body: note,
    content: note,
    note_body: note,
  });
}

export function transitionOperationsCase(caseId: string, nextStatus: string, reason: string) {
  return invokeOperationsCases({
    action: "transition",
    case_id: caseId,
    next_status: nextStatus,
    status: nextStatus,
    to_status: nextStatus,
    reason,
  });
}
